// src/pages/admin/RoleScopes.tsx - IMPROVED WITH VISUAL MATRIX
import React, { useState, useEffect, useMemo } from 'react';
import {
  Lock, Plus, Trash2, AlertTriangle, Shield, Tag,
  Check, Search, RefreshCw, Grid3X3,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Select, Loading, LogoLoadingMark, LoadingLabel } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface RoleScope {
  id: number;
  role_id: number;
  role_name: string;
  role_display_name: string;
  kategori_masalah_id: number;
  kategori_nama: string;
}

interface Role {
  id: number;
  name: string;
  display_name: string;
  level?: number;
}

interface KategoriMasalah {
  id: number;
  nama: string;
  color?: string;
}

interface DuplicateWarekAssignment {
  kategoriId: number;
  kategoriNama: string;
  roleNames: string[];
}

type ViewMode = 'list' | 'matrix';

const RoleScopes: React.FC = () => {
  const [roleScopes, setRoleScopes] = useState<RoleScope[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [kategoris, setKategoris] = useState<KategoriMasalah[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedKategori, setSelectedKategori] = useState('');
  const [deleting, setDeleting] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('matrix');
  const [searchTerm, setSearchTerm] = useState('');

  const isWarekRoleName = (roleName?: string | null): boolean => {
    return Boolean(roleName && roleName.startsWith('wakil_rektor_'));
  };

  const getRoleDisplayName = (scope: RoleScope): string => {
    return scope.role_display_name
      || roles.find(r => r.id === scope.role_id)?.display_name
      || scope.role_name
      || `Role #${scope.role_id}`;
  };

  const getWarekConflictScope = (kategoriId: number, roleId: number): RoleScope | undefined => {
    return roleScopes.find(
      s =>
        s.kategori_masalah_id === kategoriId
        && s.role_id !== roleId
        && isWarekRoleName(s.role_name)
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [scopesRes, rolesRes, kategorisRes] = await Promise.all([
        api.get('/admin/role-scopes'),
        api.get('/admin/roles'),
        api.get('/admin/kategori'),
      ]);

      setRoleScopes(scopesRes.data?.data || scopesRes.data || []);
      const rolesData = rolesRes.data?.data || rolesRes.data || [];
      setRoles(Array.isArray(rolesData) ? rolesData : []);
      const katData = kategorisRes.data?.data || kategorisRes.data || [];
      setKategoris(Array.isArray(katData) ? katData : []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddScope = async () => {
    if (!selectedRole || !selectedKategori) {
      toast.error('Pilih role dan kategori');
      return;
    }

    const roleId = Number(selectedRole);
    const kategoriId = Number(selectedKategori);

    // Cek duplikat
    const isDuplicate = roleScopes.some(
      s => s.role_id === roleId && s.kategori_masalah_id === kategoriId
    );
    if (isDuplicate) {
      toast.error('Scope sudah ada');
      return;
    }

    const role = roles.find(r => r.id === roleId);
    const kategori = kategoris.find(k => k.id === kategoriId);
    const conflictingScope = getWarekConflictScope(kategoriId, roleId);

    if (conflictingScope) {
      const ownerName = getRoleDisplayName(conflictingScope);
      const kategoriName = kategori?.nama || conflictingScope.kategori_nama || 'Kategori ini';
      toast.error(`${kategoriName} sudah dipetakan ke ${ownerName}. Satu kategori hanya boleh untuk satu Warek.`);
      return;
    }

    // ✅ FIX: Gunakan negative temp ID
    const tempId = -(Date.now());
    const tempScope: RoleScope = {
      id: tempId,
      role_id: roleId,
      role_name: role?.name || '',
      role_display_name: role?.display_name || '',
      kategori_masalah_id: kategoriId,
      kategori_nama: kategori?.nama || '',
    };
    setRoleScopes(prev => [...prev, tempScope]);

    // Tutup modal langsung
    setShowAddModal(false);
    setSelectedRole('');
    setSelectedKategori('');

    try {
      const res = await api.post('/admin/role-scopes', {
        role_id: roleId,
        kategori_masalah_id: kategoriId,
      });

      // ✅ FIX: Replace temp ID
      const realData = res.data?.data || res.data;
      const realId = realData?.id;

      if (realId) {
        setRoleScopes(prev =>
          prev.map(s => s.id === tempId ? { ...s, id: realId } : s)
        );
      } else {
        fetchData();
      }

      toast.success('Role scope berhasil ditambahkan');
    } catch (err: any) {
      // Rollback
      setRoleScopes(prev => prev.filter(s => s.id !== tempId));
      toast.error(err.response?.data?.message || 'Gagal menambahkan role scope');
    }
  };

  const handleDeleteScope = async (id: number) => {
    // ✅ FIX: Jangan hapus jika temp ID
    if (id < 0 || !Number.isInteger(id)) {
      toast.error('Data sedang diproses, mohon tunggu...');
      return;
    }

    if (!window.confirm('Yakin ingin menghapus scope ini?')) return;

    const backup = roleScopes.find(s => s.id === id);
    setRoleScopes(prev => prev.filter(s => s.id !== id));

    try {
      setDeleting(id);
      await api.delete(`/admin/role-scopes/${id}`);
      toast.success('Role scope berhasil dihapus');
    } catch (err) {
      if (backup) {
        setRoleScopes(prev => [...prev, backup]);
      }
      toast.error('Gagal menghapus role scope');
    } finally {
      setDeleting(null);
    }
  };

  const handleMatrixToggle = async (roleId: number, kategoriId: number) => {
    if (loading) return;

    const existingScope = roleScopes.find(
      s => s.role_id === roleId && s.kategori_masalah_id === kategoriId
    );

    try {
      if (existingScope) {
        // ✅ FIX: JANGAN delete jika ID bukan integer (temp ID)
        if (!Number.isInteger(existingScope.id) || existingScope.id < 0) {
          // Ini temp ID, skip delete
          toast.error('Data sedang diproses, mohon tunggu...');
          return;
        }

        // Optimistic: hapus dari state
        setRoleScopes(prev => prev.filter(s => s.id !== existingScope.id));

        await api.delete(`/admin/role-scopes/${existingScope.id}`);
        toast.success('Scope dihapus', { duration: 1500 });
      } else {
        const conflictingScope = getWarekConflictScope(kategoriId, roleId);
        if (conflictingScope) {
          const ownerName = getRoleDisplayName(conflictingScope);
          const kategoriName = kategoris.find(k => k.id === kategoriId)?.nama || conflictingScope.kategori_nama || 'Kategori ini';
          toast.error(`${kategoriName} sudah dipetakan ke ${ownerName}. Satu kategori hanya boleh untuk satu Warek.`);
          return;
        }

        // ✅ FIX: Gunakan negative temp ID yang tidak akan conflict
        const tempId = -(Date.now());
        const role = roles.find(r => r.id === roleId);
        const kategori = kategoris.find(k => k.id === kategoriId);

        const tempScope: RoleScope = {
          id: tempId,
          role_id: roleId,
          role_name: role?.name || '',
          role_display_name: role?.display_name || '',
          kategori_masalah_id: kategoriId,
          kategori_nama: kategori?.nama || '',
        };
        setRoleScopes(prev => [...prev, tempScope]);

        const res = await api.post('/admin/role-scopes', {
          role_id: roleId,
          kategori_masalah_id: kategoriId,
        });

        // ✅ FIX: Replace temp ID with real ID
        const realData = res.data?.data || res.data;
        const realId = realData?.id;

        if (realId) {
          setRoleScopes(prev =>
            prev.map(s => s.id === tempId ? { ...s, id: realId } : s)
          );
        } else {
          // Jika tidak dapat real ID, fetch ulang
          fetchData();
        }

        toast.success('Scope ditambahkan', { duration: 1500 });
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Gagal mengubah scope');
      // Rollback: fetch ulang data dari server
      fetchData();
    }
  };

  const handleToggleAll = async (roleId: number, shouldEnableAll: boolean) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    const oldScopes = [...roleScopes];
    try {
      if (shouldEnableAll) {
        const missingKategoris = kategoris.filter(kat => !hasScope(roleId, kat.id));
        const blockedKategoris = missingKategoris.filter(kat => Boolean(getWarekConflictScope(kat.id, roleId)));
        const allowedKategoris = missingKategoris.filter(kat => !getWarekConflictScope(kat.id, roleId));

        if (allowedKategoris.length === 0) {
          toast.error('Semua kategori sudah dipetakan ke Warek lain.');
          return;
        }

        // Optimistic add
        const newScopes: RoleScope[] = allowedKategoris.map(kat => ({
          id: -(Date.now() + kat.id), // ✅ FIX: Use negative unique temp ID
          role_id: roleId,
          role_name: role.name,
          role_display_name: role.display_name,
          kategori_masalah_id: kat.id,
          kategori_nama: kat.nama,
        }));

        setRoleScopes(prev => [...prev, ...newScopes]);

        for (const kat of allowedKategoris) {
          await api.post('/admin/role-scopes', {
            role_id: roleId,
            kategori_masalah_id: kat.id,
          });
        }
        if (blockedKategoris.length === 0) {
          toast.success(`Semua kategori ditambahkan untuk ${role.display_name}`);
        } else {
          toast.success(`${allowedKategoris.length} kategori ditambahkan untuk ${role.display_name}`);
        }
        if (blockedKategoris.length > 0) {
          toast.error(`${blockedKategoris.length} kategori dilewati karena sudah dipetakan ke Warek lain.`);
        }
      } else {
        const targetScopes = roleScopes.filter(s => s.role_id === roleId);
        // ✅ FIX: Filter out items with temp IDs (negative) before deletion
        const validScopesToDelete = targetScopes.filter(s => s.id > 0 && Number.isInteger(s.id));

        setRoleScopes(prev => prev.filter(s => s.role_id !== roleId));

        for (const scope of validScopesToDelete) {
          await api.delete(`/admin/role-scopes/${scope.id}`);
        }
        toast.success(`Semua kategori dihapus untuk ${role.display_name}`);
      }
      fetchData();
    } catch (err: any) {
      setRoleScopes(oldScopes);
      toast.error('Gagal mengubah massal scope');
    }
  };

  const hasScope = (roleId: number, kategoriId: number): boolean => {
    return roleScopes.some(
      s => s.role_id === roleId && s.kategori_masalah_id === kategoriId
    );
  };

  const getScopesForRole = (roleId: number): number => {
    return roleScopes.filter(s => s.role_id === roleId).length;
  };

  const getWarekScopesForKategori = (kategoriId: number): number => {
    return roleScopes.filter(
      s => s.kategori_masalah_id === kategoriId && isWarekRoleName(s.role_name)
    ).length;
  };

  const duplicateWarekAssignments = useMemo<DuplicateWarekAssignment[]>(() => {
    const grouped = new Map<number, RoleScope[]>();
    const roleDisplayNameById = new Map<number, string>(
      roles.map((role) => [role.id, role.display_name])
    );

    roleScopes.forEach((scope) => {
      if (!scope.role_name?.startsWith('wakil_rektor_')) return;
      const current = grouped.get(scope.kategori_masalah_id) || [];
      current.push(scope);
      grouped.set(scope.kategori_masalah_id, current);
    });

    return Array.from(grouped.entries())
      .map(([kategoriId, scopes]) => {
        const roleNames = Array.from(
          new Set(
            scopes.map((scope) => {
              return scope.role_display_name
                || roleDisplayNameById.get(scope.role_id)
                || scope.role_name
                || `Role #${scope.role_id}`;
            })
          )
        );
        const kategoriNama =
          kategoris.find(k => k.id === kategoriId)?.nama
          || scopes[0]?.kategori_nama
          || `Kategori #${kategoriId}`;

        return {
          kategoriId,
          kategoriNama,
          roleNames,
        };
      })
      .filter((entry) => entry.roleNames.length > 1);
  }, [roleScopes, kategoris, roles]);

  // Filter roles for matrix (only Warek level roles for Level 2 validation)
  const matrixRoles = roles.filter(r => {
    if (r.name === 'admin') return false;

    // ✅ FIX: Hanya role Wakil Rektor yang boleh punya scope
    const isWarek = r.name.startsWith('wakil_rektor_');

    if (!isWarek) return false;

    if (searchTerm && !r.display_name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    return true;
  });

  const selectedRoleId = selectedRole ? Number(selectedRole) : null;
  const selectedKategoriId = selectedKategori ? Number(selectedKategori) : null;
  const addScopeConflict = selectedRoleId && selectedKategoriId
    ? getWarekConflictScope(selectedKategoriId, selectedRoleId)
    : undefined;
  const addScopeConflictRoleName = addScopeConflict ? getRoleDisplayName(addScopeConflict) : '';
  const addScopeConflictKategoriName = selectedKategoriId
    ? (kategoris.find(k => k.id === selectedKategoriId)?.nama || addScopeConflict?.kategori_nama || 'Kategori ini')
    : 'Kategori ini';
  const canSubmitAddScope = Boolean(selectedRole && selectedKategori) && !addScopeConflict;

  if (loading) {
    return <Loading variant="page" message="Memuat role scopes..." />;
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Admin', href: '/admin' }, { label: 'Role Scopes' }]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight flex items-center gap-2">
            <Lock className="w-6 h-6 text-indigo-500" />
            Role Scopes
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Atur akses kategori untuk validasi Level 2 (Warek)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={loading ? <LogoLoadingMark size="xs" /> : <RefreshCw className="w-4 h-4" />}
            onClick={fetchData}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setShowAddModal(true)}
          >
            Tambah Scope
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="!bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-sm text-indigo-800">Informasi Role Scopes</p>
            <p className="text-xs text-indigo-700 mt-1">
              Role Scopes mengatur <strong>pembagian bidang validasi Level 2</strong> untuk setiap Wakil Rektor.
              Saat laporan masuk ke tahap validasi Level 2, sistem akan otomatis menentukan Wakil Rektor yang bertanggung jawab
              berdasarkan kategori masalah yang telah dipetakan di sini.
            </p>
            <div className="mt-3 p-2.5 bg-white/60 rounded-lg border border-indigo-200">
              <p className="text-[10px] font-semibold text-indigo-800 uppercase tracking-wider mb-2">
                Contoh Pemetaan
              </p>
              <ul className="text-xs text-indigo-700 space-y-1 list-none">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-0.5">→</span>
                  <span><strong>Warek 1</strong> diberi scope kategori <em>Akademik, Kurikulum</em> — maka semua laporan berkategori tersebut akan diarahkan ke Warek 1</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-0.5">→</span>
                  <span><strong>Warek 2</strong> diberi scope kategori <em>SDM, Keuangan</em> — maka laporan terkait akan diarahkan ke Warek 2</span>
                </li>
              </ul>
            </div>
            <p className="text-[10px] text-indigo-500 mt-2">
              Pastikan setiap kategori masalah sudah dipetakan ke Wakil Rektor yang sesuai agar proses validasi berjalan otomatis.
              Kategori yang belum dipetakan tidak akan memiliki validator Level 2.
            </p>
          </div>
        </div>
      </Card>

      {duplicateWarekAssignments.length > 0 && (
        <Card className="!bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-sm text-red-800">Peringatan Konflik Scope Warek</p>
              <p className="text-xs text-red-700 mt-1">
                Ditemukan kategori yang dipetakan ke lebih dari satu Wakil Rektor. Ini bisa menimbulkan bug penentuan validator Level 2.
              </p>
              <ul className="mt-2 space-y-1 list-none">
                {duplicateWarekAssignments.map((entry) => (
                  <li key={entry.kategoriId} className="text-xs text-red-700">
                    <strong>{entry.kategoriNama}:</strong> {entry.roleNames.join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="!p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center shadow-sm">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-black text-blue-600">{roles.length}</p>
              <p className="text-[10px] text-blue-600/70 font-semibold uppercase">Total Roles</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4 bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center shadow-sm">
              <Tag className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-black text-orange-600">{kategoris.length}</p>
              <p className="text-[10px] text-orange-600/70 font-semibold uppercase">Kategori</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4 bg-gradient-to-br from-teal-50 to-teal-100/50 border-teal-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center shadow-sm">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-black text-teal-600">{roleScopes.length}</p>
              <p className="text-[10px] text-teal-600/70 font-semibold uppercase">Total Scopes</p>
            </div>
          </div>
        </Card>
      </div>

      {/* View Mode Toggle & Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
          <input
            type="text"
            placeholder="Cari role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-main/20 focus:border-teal-main"
          />
        </div>

        <div className="flex items-center gap-1 p-1 bg-neutral-bg rounded-xl">
          <button
            onClick={() => setViewMode('matrix')}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${viewMode === 'matrix'
              ? 'bg-white text-teal-main shadow-sm'
              : 'text-neutral-text-secondary hover:text-neutral-text-primary'
              }`}
          >
            <Grid3X3 className="w-4 h-4" />
            Matrix
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${viewMode === 'list'
              ? 'bg-white text-teal-main shadow-sm'
              : 'text-neutral-text-secondary hover:text-neutral-text-primary'
              }`}
          >
            <Lock className="w-4 h-4" />
            List
          </button>
        </div>
      </div>

      {/* MATRIX VIEW */}
      {viewMode === 'matrix' && (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-neutral-bg to-gray-100">
                  <th className="sticky left-0 z-10 bg-neutral-bg px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-neutral-text-secondary border-r border-neutral-border min-w-[200px]">
                    Role
                  </th>
                  {kategoris.map((kat) => {
                    const warekScopeCount = getWarekScopesForKategori(kat.id);
                    const hasConflict = warekScopeCount > 1;

                    return (
                      <th
                        key={kat.id}
                        className="px-3 py-3 text-center min-w-[100px]"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: kat.color || '#666' }}
                          >
                            <Tag className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-[10px] font-semibold text-neutral-text-primary leading-tight">
                            {kat.nama}
                          </span>
                          <span className={`text-[9px] ${hasConflict ? 'text-red-600 font-semibold' : 'text-neutral-text-secondary'}`}>
                            {warekScopeCount} warek{hasConflict ? ' - Konflik' : ''}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-border/50">
                {matrixRoles.map((role) => {
                  const scopeCount = getScopesForRole(role.id);
                  const isAdmin = role.name === 'admin';

                  return (
                    <tr key={role.id} className="hover:bg-neutral-bg/50 transition-colors">
                      <td className="sticky left-0 z-10 bg-white px-4 py-3 border-r border-neutral-border shadow-[2px_0_5px_rgba(0,0,0,0.05)]">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                              <Shield className="w-4 h-4 text-indigo-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-neutral-text-primary truncate" title={role.display_name}>
                                {role.display_name}
                              </p>
                              <p className="text-[10px] text-neutral-text-secondary font-mono">
                                {role.name}
                              </p>
                            </div>
                          </div>

                          {/* Toggle All Button */}
                          {!isAdmin && (
                            <button
                              onClick={() => handleToggleAll(role.id, scopeCount < kategoris.length)}
                              className={`
                                ml-2 p-1 rounded-md border transition-all
                                ${scopeCount === kategoris.length
                                  ? 'bg-teal-50 border-teal-200 text-teal-600 hover:bg-teal-100'
                                  : 'bg-neutral-bg border-neutral-border text-neutral-text-secondary hover:bg-neutral-border/50'
                                }
                              `}
                              title={scopeCount === kategoris.length ? 'Deselect all' : 'Select all'}
                            >
                              <Check className={`w-3.5 h-3.5 ${scopeCount === kategoris.length ? 'opacity-100' : 'opacity-30'}`} />
                            </button>
                          )}
                        </div>
                      </td>
                      {kategoris.map((kat) => {
                        const hasAccess = isAdmin || hasScope(role.id, kat.id);

                        return (
                          <td key={kat.id} className="px-3 py-3 text-center">
                            <button
                              onClick={() => !isAdmin && handleMatrixToggle(role.id, kat.id)}
                              disabled={isAdmin}
                              className={`
                                w-10 h-10 rounded-xl flex items-center justify-center mx-auto transition-all
                                ${isAdmin
                                  ? 'bg-teal-100 cursor-not-allowed'
                                  : hasAccess
                                    ? 'bg-teal-500 hover:bg-teal-600 shadow-md hover:shadow-lg hover:scale-105'
                                    : 'bg-neutral-bg hover:bg-neutral-border/50 border-2 border-dashed border-neutral-border'
                                }
                              `}
                              title={isAdmin ? 'Admin has all access' : hasAccess ? 'Click to remove' : 'Click to add'}
                            >
                              {hasAccess ? (
                                <Check className="w-5 h-5 text-white" />
                              ) : (
                                <Plus className="w-4 h-4 text-neutral-text-secondary" />
                              )}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="px-4 py-3 border-t border-neutral-border bg-neutral-bg/50">
            <div className="flex items-center gap-6 text-xs text-neutral-text-secondary">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-teal-500 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
                <span>Memiliki Akses</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-neutral-bg border-2 border-dashed border-neutral-border flex items-center justify-center">
                  <Plus className="w-3 h-3 text-neutral-text-secondary" />
                </div>
                <span>Belum Ada Akses</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-teal-600" />
                </div>
                <span>Admin (Semua Akses)</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-neutral-border bg-neutral-bg">
                <tr>
                  <th className="px-4 py-3 text-left font-bold text-neutral-text-secondary uppercase text-xs tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-neutral-text-secondary uppercase text-xs tracking-wider">
                    Kategori Masalah
                  </th>
                  <th className="px-4 py-3 text-right font-bold text-neutral-text-secondary uppercase text-xs tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-border/50">
                {roleScopes.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-12 text-center">
                      <Lock className="w-12 h-12 mx-auto mb-3 text-neutral-text-secondary opacity-30" />
                      <p className="text-sm text-neutral-text-secondary">Belum ada role scope</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => setShowAddModal(true)}
                      >
                        Tambah Pertama
                      </Button>
                    </td>
                  </tr>
                ) : (
                  roleScopes.map((scope) => {
                    const kategori = kategoris.find(k => k.nama === scope.kategori_nama || k.id === scope.kategori_masalah_id);

                    return (
                      <tr key={scope.id} className="hover:bg-neutral-bg/50 transition-colors group">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shrink-0">
                              <Shield className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-neutral-text-primary">
                                {scope.role_display_name}
                              </p>
                              <p className="text-[10px] text-neutral-text-secondary font-mono">
                                {scope.role_name}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded-md"
                              style={{ backgroundColor: kategori?.color || '#666' }}
                            />
                            <span className="text-neutral-text-primary font-medium">
                              {scope.kategori_nama}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="danger"
                            size="sm"
                            icon={deleting === scope.id ? <LogoLoadingMark size="xs" /> : <Trash2 className="w-4 h-4" />}
                            onClick={() => handleDeleteScope(scope.id)}
                            disabled={deleting === scope.id}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            {deleting === scope.id ? <LoadingLabel label="Menghapus..." /> : 'Hapus'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="➕ Tambah Role Scope"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Batal</Button>
            <Button variant="primary" onClick={handleAddScope} disabled={!canSubmitAddScope}>Tambah</Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-text-secondary">
            Pilih role dan kategori masalah yang dapat divalidasi
          </p>

          <Select
            label="Role"
            value={selectedRole}
            onChange={setSelectedRole}
            placeholder="Pilih Wakil Rektor..."
            options={roles
              .filter(r => {
                // ✅ FIX: Hanya Wakil Rektor
                return r.name.startsWith('wakil_rektor_');
              })
              .map(r => ({
                value: r.id.toString(),
                label: r.display_name,
              }))}
          />

          <Select
            label="Kategori Masalah"
            value={selectedKategori}
            onChange={setSelectedKategori}
            placeholder="Pilih Kategori..."
            options={kategoris.map(k => ({
              value: k.id.toString(),
              label: k.nama,
            }))}
          />

          {addScopeConflict && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <p className="text-xs font-semibold text-red-700">
                {addScopeConflictKategoriName} sudah dipetakan ke {addScopeConflictRoleName}.
              </p>
              <p className="text-[11px] text-red-600 mt-0.5">
                Satu kategori hanya boleh untuk satu Wakil Rektor.
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default RoleScopes;
